import {
  Box,
  Divider,
  HStack,
  Link as ChakraLink,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { Button } from "./components/Button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import actions from "src/actions";
import type {
  DisplayMarketingAdSet,
  DisplayMarketingAd,
  DisplayMarketingCampaign,
  MarketingCampaign,
} from "@common/interfaces/KYDMarketing";
import { PublisherPlatforms } from "@common/interfaces/KYDMarketing";
import { normalizeGuidedEditTargetAdsetIds } from "@common/utils/guidedEditTargetAdsets";
import { deriveCampaignFlightIsoBoundsFromAdsets } from "@common/utils/campaignBudgetMeta";
import {
  ADSET_FINISHED_DELIVERY_EDIT_GUIDANCE,
  anyListedAdsetFinishedDelivery,
  hasAdsetFinishedDelivery,
} from "@common/utils/adsetDelivery";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import {
  fetchAdvertiserIdentityData,
  fetchAdvertiserIdentityInstagramAccounts,
  fetchAdvertiserIdentityPages,
  partnershipIdentityCatalogToEditorState,
  type PartnershipPartnerOptionsLoad,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/fetchPartnershipPartnerOptions";
import { mergePartnerFacebookPagesForPicker } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/mapPartnershipPartnerOptionsResponse";
import { fetchInstagramAccountsForAdvertiserPage } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/fetchInstagramAccountsForAdvertiserPage";
import { mergeInstagramAccounts } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/mergeInstagramAccounts";
import { fetchInstagramAccountsForFacebookPages } from "src/Manager/Campaigns/Ads/utils/fetchInstagramAccountsForFacebookPages";
import TabSelector from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/TabSelector";
import { useManagedCampaigns } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/hooks/useManagedCampaigns";
import { useDedupedPartnershipPartnerOptionsRefetch } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/hooks/useDedupedPartnershipPartnerOptionsRefetch";
import CampaignDetailsCard from "./CampaignDetailsCard";
import CampaignResultsCard from "./CampaignResultsCard";
import CampaignMetricsCard from "./CampaignMetricsCard";
import RightPanel from "./RightPanel";
import { CampaignPublishProgressIndicator } from "./components/CampaignPublishProgressIndicator";
import {
  mergePublishLoadOutcomes,
  PUBLISH_HEADER_OUTCOME_MS,
  type CampaignPublishCheckResponse,
  type PublishLoadOutcome,
} from "./publishPollingUtils";
import { mergeDisplayCampaignFromMarketingUpdate } from "./utils/mergeDisplayCampaignFromMarketingUpdate";
import { MetaSyncStatusLabel } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/StatusBadge";
import GuidedCreateCampaignFlowScreen from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedCreateCampaignFlowScreen";
import { mapCreativeBlockToGuidedCreative } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/mapCreativeBlockToGuidedCreative";
import { isManagedAdsPartnerPostsEnabledForOrg } from "src/managedAdsPartnerPostsAccess";
import {
  normalizeGuidedFlowEventTimezone,
  type GuidedFlowDraft,
  type GuidedEditPayload,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreateCampaignModal";
import type { GuidedEditCreativeApi } from "@common/interfaces/adsManagerGuidedEdit";
import EditBudgetModal from "./components/EditBudgetModal";
import EditScheduleModal, {
  type ScheduleInput,
} from "./components/EditScheduleModal";
import EditAdCreativeModal from "./components/EditAdCreativeModal";
import type { BudgetData } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedBudgetEditor/BudgetSelector";
import {
  buildBudgetUpdatePayload,
  buildCreativeUpdatePayload,
  buildScheduledBudgetDataForEditModal,
  buildScheduleUpdatePayload,
  coerceGuidedEditPayload,
  isGuidedEditPayloadBudgetScheduled,
} from "./utils/focusedEditPayload";
import {
  guidedEditPutUpdateId,
} from "./utils/guidedEditPutResponse";
import { shouldLockGuidedScheduleStartEdit } from "./utils/shouldLockGuidedScheduleStartEdit";
import { wireGuidedEditBudgetFromFormDollars } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/wireGuidedEditBudgetFromForm";
import {
  isGuidedEditNothingToLoadFetchError,
  messageForManagedAdsetEditUnavailable,
  resolveManagedAdsetEditUnavailable,
} from "@common/utils/adsetSchedule";
import type {
  CreativeBlock,
  GuidedSubmitParams,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/guidedFlow/types";
import { wireScheduleToEventWall } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/eventWallTime";
import {
  CampaignStatus,
  type ActionableCampaignIntent,
} from "common/interfaces/KYDMarketing";
import {
  getIntentApplyOutcomeDescription,
  getIntentApplyOutcomeTitle,
} from "common/interfaces/CampaignIntent";
import type { KYDScope } from "@common/interfaces/ICognito";
import { useCampaignPublishPolling } from "./useCampaignPublishPolling";
import MetaCampaignToolsModal from "./MetaCampaignToolsModal";
import KYDLoading from "src/ui/KYDLoading";
import { collectFacebookPageIdsFromCampaign, collectAdvertiserIdentityFromCampaign } from "./utils/adDisplayIdentity";

const LINKED_SCHEDULED_BUDGET_SCHEDULE_READONLY_MESSAGE =
  "Campaigns with a scheduled budget that were synced to Meta cannot change their run dates.";

type FocusedBudgetEditState = {
  adsetId: string;
  budget: BudgetData;
};

type FocusedScheduleEditState = {
  adsetId: string;
  eventTimezone: string;
  schedule: ScheduleInput;
  isStartTimeLocked: boolean;
  isScheduleRangeReadOnly?: boolean;
  scheduleReadOnlyHelperText?: string;
};

type FocusedCreativeEditState = {
  adsetId: string;
  creativeIndex: number;
  creatives: GuidedEditPayload["creatives"];
  creative: GuidedEditPayload["creatives"][number];
};

type GuidedEditPutResponse = {
  update_id?: string;
  campaign?: MarketingCampaign;
};

export default function CampaignDetail({
  permissions,
}: {
  /** User scopes; Meta tools panel (double-click campaign name) requires `platforms/read` or `platforms/write`; import needs `platforms/write`. */
  permissions?: KYDScope[];
} = {}) {
  const { campaign_id } = useParams<{ campaign_id: string }>();
  const userScopes = permissions ?? [];
  const canImportFromMeta = userScopes.includes("platforms/write");
  const hasPlatformScopes = userScopes.some(
    (s) => s === "platforms/read" || s === "platforms/write"
  );
  const {
    isOpen: isMetaToolsOpen,
    onOpen: onOpenMetaTools,
    onClose: onCloseMetaTools,
  } = useDisclosure();
  const [campaign, setCampaign] = useState<DisplayMarketingCampaign | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [catalogPartnerPages, setCatalogPartnerPages] = useState<FacebookPage[]>(
    []
  );
  const [partnerInstagramAccounts, setPartnerInstagramAccounts] = useState<
    InstagramAccount[]
  >([]);
  const [instagramAccounts, setInstagramAccounts] = useState<
    InstagramAccount[]
  >([]);
  const [partnershipOptionsLoading, setPartnershipOptionsLoading] =
    useState(false);
  const [partnershipOptionsError, setPartnershipOptionsError] = useState<
    string | null
  >(null);
  const [partnershipPermissionsLoaded, setPartnershipPermissionsLoaded] =
    useState(false);
  const [advertiserValidationMessages, setAdvertiserValidationMessages] =
    useState<string[]>([]);
  const [metaBusinessId, setMetaBusinessId] = useState<string | undefined>();
  const [creativeModalInstagramLoading, setCreativeModalInstagramLoading] =
    useState(false);
  const [guidedFlowDraft, setGuidedFlowDraft] =
    useState<GuidedFlowDraft | null>(null);
  const [guidedPublishStatus, setGuidedPublishStatus] = useState<string | null>(
    null
  );
  /** Drives header success/error after managed `pollForResult` (edits, publish, etc.). */
  const [managedPublishLoadOutcome, setManagedPublishLoadOutcome] =
    useState<PublishLoadOutcome>("idle");

  const onManagedPollDone = useCallback((ok: boolean) => {
    setGuidedPublishStatus(null);
    setManagedPublishLoadOutcome(ok ? "success" : "error");
    window.setTimeout(
      () => setManagedPublishLoadOutcome("idle"),
      PUBLISH_HEADER_OUTCOME_MS
    );
  }, []);
  const [pixels, setPixels] = useState<{ id: string; name: string }[]>([]);
  const [isRelatedDataLoading, setIsRelatedDataLoading] = useState(false);
  const [generatingIntents, setGeneratingIntents] = useState(false);
  const [isPausingCampaign, setIsPausingCampaign] = useState(false);
  const [isPublishingCampaign, setIsPublishingCampaign] = useState(false);
  const [pausingAdsetId, setPausingAdsetId] = useState<string | null>(null);
  const [publishingAdsetId, setPublishingAdsetId] = useState<string | null>(
    null
  );
  const [activatingAdKey, setActivatingAdKey] = useState<string | null>(null);
  const [pausingAdKey, setPausingAdKey] = useState<string | null>(null);
  const [budgetEditState, setBudgetEditState] =
    useState<FocusedBudgetEditState | null>(null);
  const [scheduleEditState, setScheduleEditState] =
    useState<FocusedScheduleEditState | null>(null);
  const [creativeEditState, setCreativeEditState] =
    useState<FocusedCreativeEditState | null>(null);
  const [savingFocusedEdit, setSavingFocusedEdit] = useState(false);
  /** GET guided-edit payload in flight after user clicks an edit entry (modals / full editor). */
  const [isGuidedEditPayloadLoading, setIsGuidedEditPayloadLoading] =
    useState(false);
  const [isMetaImportApplyLoading, setIsMetaImportApplyLoading] =
    useState(false);
  const toast = useToast();
  /** When true, layout is column and we show tabs at top to switch Overview vs Insights (same idea as BuildCampaign mobile tabs). */
  const isNarrow = useMediaQuery("(max-width: 991px)")[0];
  const [mobileTab, setMobileTab] = useState<"overview" | "insights">(
    "overview"
  );

  const creativeTargetAdsetOptions = (campaign?.adsets ?? [])
    .filter(
      (adset) => adset.status !== "DELETED" && adset.status !== "TO_DELETE"
    )
    .map((adset) => ({
      id: adset.id,
      name: adset.name || "Untitled ad set",
    }));

  const addMenuBlockedByPastAdsetSchedule = useMemo(
    () => anyListedAdsetFinishedDelivery(campaign?.adsets),
    [campaign?.adsets]
  );

  const partnerPostsEnabled = isManagedAdsPartnerPostsEnabledForOrg(
    campaign?.organization_id
  );

  const fetchCampaign = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!campaign_id) return;
      const silent = opts?.silent === true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const data = await actions.ADMIN.fetchCampaign(campaign_id, {
          include_event: true,
        });
        const c =
          (data as { campaign?: DisplayMarketingCampaign })?.campaign ?? data;
        setCampaign(c as DisplayMarketingCampaign);
      } catch (err: any) {
        if (!silent) {
          setError(err?.message ?? "Failed to load campaign");
          toast({
            title: "Error loading campaign",
            description: err?.message,
            status: "error",
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [campaign_id, toast]
  );

  /** Refetch campaign without showing full-page loading (e.g. after intent apply/ignore). */
  const handleRefetchSilent = useCallback(
    () => fetchCampaign({ silent: true }),
    [fetchCampaign]
  );

  const {
    pollForResult,
    searchInterests,
    searchGeoMarkets,
    loading: managedLoading,
  } = useManagedCampaigns({
    defaultCampaign: null,
    defaultEditingAdset: null,
    pages,
    instagramAccounts,
    onPollingComplete: handleRefetchSilent,
  });

  const applyGuidedEditApiCampaignToState = useCallback(
    (response: GuidedEditPutResponse) => {
      const next = response.campaign;
      if (!next?.id) return;
      setCampaign((prev) => {
        if (!prev || prev.id !== next.id) return prev;
        return mergeDisplayCampaignFromMarketingUpdate(prev, next);
      });
    },
    []
  );

  const onCampaignUpdatePollPayload = useCallback(
    (payload: CampaignPublishCheckResponse) => {
      if (payload.status !== "PROCESSING" || payload.request == null) {
        return;
      }
      setCampaign((prev) => {
        if (!prev || !campaign_id || prev.id !== campaign_id) {
          return prev;
        }
        return mergeDisplayCampaignFromMarketingUpdate(
          prev,
          payload.request as MarketingCampaign
        );
      });
    },
    [campaign_id]
  );

  const managedCampaignPollOptions = useMemo(
    () => ({
      onStatusUpdate: setGuidedPublishStatus,
      onDone: onManagedPollDone,
      onPollPayload: onCampaignUpdatePollPayload,
    }),
    [onCampaignUpdatePollPayload, onManagedPollDone]
  );

  const pollPublishAfterIntentApply = useCallback(
    async ({
      campaign_id: cid,
      update_id,
      intent,
    }: {
      campaign_id: string;
      update_id: string;
      intent: ActionableCampaignIntent;
    }) => {
      setGuidedPublishStatus("Publishing changes…");
      const description = getIntentApplyOutcomeDescription(intent);
      await pollForResult({
        campaign_id: cid,
        update_id,
        title: getIntentApplyOutcomeTitle(intent),
        description,
        ...managedCampaignPollOptions,
      });
    },
    [managedCampaignPollOptions, pollForResult]
  );

  const {
    serverPublishStep,
    headerPublishLockToken,
    isPublishPollingActive,
    urlPublishLoadOutcome,
  } = useCampaignPublishPolling({
    campaignId: campaign_id,
    campaignExecutionId: campaign?.execution_id,
    refetchCampaign: handleRefetchSilent,
  });

  const publishLoadOutcome = mergePublishLoadOutcomes(
    urlPublishLoadOutcome,
    managedPublishLoadOutcome
  );

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  const campaignFacebookPageIdsKey = useMemo(
    () => collectFacebookPageIdsFromCampaign(campaign).sort().join(","),
    [campaign]
  );

  const partnerPages = useMemo(
    () => mergePartnerFacebookPagesForPicker(pages, catalogPartnerPages),
    [pages, catalogPartnerPages]
  );

  const applyPartnershipIdentityLoad = useCallback(
    (catalog: PartnershipPartnerOptionsLoad) => {
      const slices = partnershipIdentityCatalogToEditorState(catalog);
      setCatalogPartnerPages(slices.partnerPages);
      setPartnerInstagramAccounts(slices.partnerInstagramAccounts);
      setAdvertiserValidationMessages(slices.advertiserValidationMessages);
      setPartnershipPermissionsLoaded(slices.partnershipPermissionsLoaded);
      setMetaBusinessId(slices.metaBusinessId);
      setPartnershipOptionsError(null);
    },
    []
  );

  const { refetch: refetchPartnershipPartnerOptions, catalogByKey } =
    useDedupedPartnershipPartnerOptionsRefetch(
      applyPartnershipIdentityLoad,
      setPartnershipOptionsLoading,
      setPartnershipOptionsError
    );

  const refetchAdvertiserIdentityForAccount = useCallback(
    async (adAccountId: string) => {
      const accountId = adAccountId.trim();
      if (!accountId) return;
      setIsRelatedDataLoading(true);
      try {
        const { pages: identityPages, instagramAccounts: identityInstagramAccounts } =
          await fetchAdvertiserIdentityData({ ad_account_id: accountId });
        setPages(identityPages);
        setInstagramAccounts(identityInstagramAccounts);
      } catch (e) {
        setPartnershipOptionsError(
          e instanceof Error
            ? e.message
            : "Could not load Facebook pages and Instagram accounts"
        );
      } finally {
        setIsRelatedDataLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!campaign?.account_id) return;

    const adAccountId = campaign.account_id;
    const campaignIdentity = collectAdvertiserIdentityFromCampaign(campaign);
    const campaignPageIds = campaignFacebookPageIdsKey
      ? campaignFacebookPageIdsKey.split(",").filter(Boolean)
      : [];
    const primaryAdvertiserPageId = campaignPageIds[0];

    setIsRelatedDataLoading(true);
    let cancelled = false;

    const instagramCatalogPromise =
      campaignPageIds.length > 0
        ? fetchInstagramAccountsForFacebookPages(
            adAccountId,
            campaignPageIds,
            actions.ADMIN.listInstagramAccounts
          ).catch(() => [] as InstagramAccount[])
        : fetchAdvertiserIdentityInstagramAccounts({
            ad_account_id: adAccountId,
          }).catch(() => [] as InstagramAccount[]);

    // Advertiser pages + page-scoped IG for this campaign; partnership options when enabled.
    const partnershipFetch = partnerPostsEnabled
      ? refetchPartnershipPartnerOptions({
          ad_account_id: adAccountId,
          advertiser_page_id:
            campaignIdentity.pageId ?? primaryAdvertiserPageId,
          advertiser_instagram_user_id: campaignIdentity.instagramUserId,
        })
      : Promise.resolve();

    Promise.all([
      partnershipFetch,
      actions.ADMIN.listPixels({ ad_account_id: adAccountId })
        .then((r: unknown) => (r as { id: string; name: string }[]) ?? [])
        .catch(() => [] as { id: string; name: string }[]),
      fetchAdvertiserIdentityPages({ ad_account_id: adAccountId }).catch(
        () => [] as FacebookPage[]
      ),
      instagramCatalogPromise,
    ])
      .then(([, pixelsResponse, identityPages, identityInstagramAccounts]) => {
        if (cancelled) return;
        setPixels(pixelsResponse);
        setPages(identityPages);
        setInstagramAccounts(identityInstagramAccounts);
      })
      .catch((e) => {
        if (cancelled) return;
        setPartnershipOptionsError(
          e instanceof Error
            ? e.message
            : "Could not load campaign identity data"
        );
      })
      .finally(() => {
        if (!cancelled) setIsRelatedDataLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    campaign,
    campaignFacebookPageIdsKey,
    partnerPostsEnabled,
    refetchPartnershipPartnerOptions,
  ]);

  const handlePauseCampaign = useCallback(async () => {
    if (!campaign?.id) return;
    setIsPausingCampaign(true);
    setGuidedPublishStatus("Pausing campaign…");
    try {
      await actions.ADMIN.unpublishCampaign({ campaign_id: campaign.id });
      toast({ title: "Campaign paused", status: "success" });
      await fetchCampaign({ silent: true });
    } catch (err: any) {
      toast({
        title: "Failed to pause campaign",
        description: err?.message,
        status: "error",
      });
    } finally {
      setGuidedPublishStatus(null);
      setIsPausingCampaign(false);
    }
  }, [campaign?.id, fetchCampaign, toast]);

  const handleUnpublishCampaign = useCallback(
    async (payload: { id: string }) => {
      setGuidedPublishStatus("Pausing campaign…");
      try {
        await actions.ADMIN.unpublishCampaign({ campaign_id: payload.id });
        toast({ title: "Campaign paused", status: "success" });
        await fetchCampaign({ silent: true });
      } catch (err: any) {
        toast({
          title: "Failed to pause campaign",
          description: err?.message,
          status: "error",
        });
      } finally {
        setGuidedPublishStatus(null);
      }
    },
    [fetchCampaign, toast]
  );

  const handlePublishAdset = useCallback(
    async (payload: { campaign_id: string; adset_id: string }) => {
      setPublishingAdsetId(payload.adset_id);
      try {
        const response = (await actions.ADMIN.publishAdset(payload)) as {
          update_id?: string;
        };
        if (response?.update_id) {
          setGuidedPublishStatus("Activating ad set…");
          await pollForResult({
            campaign_id: payload.campaign_id,
            update_id: response.update_id,
            title: "Ad set activated",
            description: "Ad set activated",
            ...managedCampaignPollOptions,
          });
        } else {
          toast({ title: "Ad set activated", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to activate ad set",
          description: err?.message,
          status: "error",
        });
      } finally {
        setPublishingAdsetId(null);
      }
    },
    [fetchCampaign, managedCampaignPollOptions, pollForResult, toast]
  );

  const handleUnpublishAdset = useCallback(
    async (payload: { campaign_id: string; adset_id: string }) => {
      setPausingAdsetId(payload.adset_id);
      setGuidedPublishStatus("Pausing ad set…");
      try {
        await actions.ADMIN.unpublishAdset(payload);
        toast({ title: "Ad set paused", status: "success" });
        await fetchCampaign({ silent: true });
      } catch (err: any) {
        toast({
          title: "Failed to pause ad set",
          description: err?.message,
          status: "error",
        });
      } finally {
        setGuidedPublishStatus(null);
        setPausingAdsetId(null);
      }
    },
    [fetchCampaign, toast]
  );

  const handleUnpublishAd = useCallback(
    async (payload: {
      campaign_id: string;
      adset_id: string;
      ad_id: string;
    }) => {
      const key = `${payload.adset_id}:${payload.ad_id}`;
      setPausingAdKey(key);
      setGuidedPublishStatus("Pausing ad…");
      try {
        const response = (await actions.ADMIN.unpublishAd(payload)) as {
          update_id?: string;
        };
        if (response?.update_id) {
          await pollForResult({
            campaign_id: payload.campaign_id,
            update_id: response.update_id,
            title: "Ad paused",
            description: "Ad paused",
            ...managedCampaignPollOptions,
          });
        } else {
          toast({ title: "Ad paused", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to pause ad",
          description: err?.message,
          status: "error",
        });
      } finally {
        setGuidedPublishStatus(null);
        setPausingAdKey(null);
      }
    },
    [fetchCampaign, managedCampaignPollOptions, pollForResult, toast]
  );

  const handlePublishAd = useCallback(
    async (payload: {
      campaign_id: string;
      adset_id: string;
      ad_id: string;
    }) => {
      const key = `${payload.adset_id}:${payload.ad_id}`;
      setActivatingAdKey(key);
      try {
        const response = (await actions.ADMIN.publishAd(payload)) as {
          update_id?: string;
        };
        if (response?.update_id) {
          setGuidedPublishStatus("Activating ad…");
          await pollForResult({
            campaign_id: payload.campaign_id,
            update_id: response.update_id,
            title: "Ad activated",
            description: "Ad activated",
            ...managedCampaignPollOptions,
          });
        } else {
          toast({ title: "Ad activated", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to activate ad",
          description: err?.message,
          status: "error",
        });
      } finally {
        setActivatingAdKey(null);
      }
    },
    [fetchCampaign, managedCampaignPollOptions, pollForResult, toast]
  );

  const handlePublishCampaign = useCallback(
    async (payload: { id: string }) => {
      setIsPublishingCampaign(true);
      try {
        const response = (await actions.ADMIN.publishCampaign({
          campaign_id: payload.id,
          publish_all_adsets: true,
        })) as { update_id?: string };
        if (response?.update_id) {
          setGuidedPublishStatus("Activating campaign…");
          await pollForResult({
            campaign_id: payload.id,
            update_id: response.update_id,
            title: "Campaign activated",
            description: "Campaign activated",
            ...managedCampaignPollOptions,
          });
        } else {
          toast({ title: "Campaign activated", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to activate campaign",
          description: err?.message,
          status: "error",
        });
      } finally {
        setIsPublishingCampaign(false);
      }
    },
    [fetchCampaign, managedCampaignPollOptions, pollForResult, toast]
  );

  /** When `adsetId` is omitted, the dashboard API picks the first editable ad set server-side. */
  const fetchGuidedPayloadForAdset = useCallback(
    async (adsetId?: string) => {
      if (!campaign?.id) {
        throw new Error("Campaign not loaded");
      }
      const raw =
        adsetId != null && String(adsetId).trim() !== ""
          ? await actions.ADMIN.fetchGuidedEditPayloadForAdset({
              campaign_id: campaign.id,
              adset_id: adsetId,
            })
          : await actions.ADMIN.fetchGuidedEditPayloadForCampaign({
              campaign_id: campaign.id,
            });
      return coerceGuidedEditPayload(raw);
    },
    [campaign?.id]
  );

  const openFocusedBudgetEdit = useCallback(async () => {
    setIsGuidedEditPayloadLoading(true);
    try {
      const payload = await fetchGuidedPayloadForAdset();
      const resolvedAdsetId = payload.adset_id;
      const budgetFromApi = payload.budget;
      const tz = normalizeGuidedFlowEventTimezone(
        campaign?.event?.timezone ?? payload.timezone
      );
      if (isGuidedEditPayloadBudgetScheduled(budgetFromApi)) {
        setBudgetEditState({
          adsetId: resolvedAdsetId,
          budget: buildScheduledBudgetDataForEditModal({
            eventTimezone: tz,
            segments: budgetFromApi.segments.map((s) => ({
              time_start: s.time_start,
              time_end: s.time_end,
              amountMinor: s.amount,
              currency: s.currency,
            })),
          }),
        });
        return;
      }
      if (
        campaign?.budget?.type === "scheduled" &&
        (campaign.budget.segments?.length ?? 0) > 0
      ) {
        setBudgetEditState({
          adsetId: resolvedAdsetId,
          budget: buildScheduledBudgetDataForEditModal({
            eventTimezone: tz,
            segments: (campaign.budget.segments ?? []).map((s) => ({
              time_start: s.time_start,
              time_end: s.time_end,
              amountMinor: s.amount?.amount ?? 0,
              currency: s.amount?.currency ?? "USD",
            })),
          }),
        });
        return;
      }
      // GET amount is cents; editor + BudgetData use dollars (see wireGuidedEditBudgetFromFormDollars on save)
      const amountCents =
        budgetFromApi?.amount != null ? Number(budgetFromApi.amount) : null;
      const amountDollars =
        amountCents != null ? Math.round(amountCents) / 100 : null;
      setBudgetEditState({
        adsetId: resolvedAdsetId,
        budget: {
          type: budgetFromApi?.type ?? "lifetime",
          amount: {
            amount: amountDollars,
            currency: "USD",
          },
        },
      });
    } catch (err: any) {
      if (isGuidedEditNothingToLoadFetchError(err)) {
        toast({
          title: "You can't edit the budget right now",
          description:
            typeof err?.message === "string"
              ? err.message
              : "This campaign needs at least one ad set before budget changes are possible.",
          status: "warning",
        });
      } else {
        toast({
          title: "Something went wrong",
          description: err?.message ?? String(err),
          status: "error",
        });
      }
    } finally {
      setIsGuidedEditPayloadLoading(false);
    }
  }, [
    campaign?.adsets,
    campaign?.budget,
    campaign?.event?.timezone,
    fetchGuidedPayloadForAdset,
    toast,
  ]);

  const openFocusedScheduleEdit = useCallback(async () => {
    setIsGuidedEditPayloadLoading(true);
    try {
      const payload = await fetchGuidedPayloadForAdset();
      const resolvedAdsetId = payload.adset_id;
      const scheduleFromApi = payload.schedule;
      const tz = normalizeGuidedFlowEventTimezone(
        campaign?.event?.timezone ?? payload.timezone
      );
      const isStartTimeLocked = shouldLockGuidedScheduleStartEdit({
        campaignExternalId: campaign?.external_id,
        serverStartAt: scheduleFromApi?.start_at,
        eventIanaTimezone: tz,
      });
      const isScheduleRangeReadOnly =
        Boolean(String(campaign?.external_id ?? "").trim()) &&
        payload.budget?.type === "scheduled";
      setScheduleEditState({
        adsetId: resolvedAdsetId,
        eventTimezone: tz,
        schedule: {
          start_at: wireScheduleToEventWall(scheduleFromApi?.start_at, tz),
          end_at: wireScheduleToEventWall(scheduleFromApi?.end_at, tz),
        },
        isStartTimeLocked: isStartTimeLocked && !isScheduleRangeReadOnly,
        isScheduleRangeReadOnly,
        scheduleReadOnlyHelperText: isScheduleRangeReadOnly
          ? LINKED_SCHEDULED_BUDGET_SCHEDULE_READONLY_MESSAGE
          : undefined,
      });
    } catch (err: any) {
      if (isGuidedEditNothingToLoadFetchError(err)) {
        toast({
          title: "You can't edit the schedule right now",
          description:
            typeof err?.message === "string"
              ? err.message
              : "This campaign needs at least one ad set before schedule changes are possible.",
          status: "warning",
        });
      } else {
        toast({
          title: "Something went wrong",
          description: err?.message ?? String(err),
          status: "error",
        });
      }
    } finally {
      setIsGuidedEditPayloadLoading(false);
    }
  }, [
    campaign?.adsets,
    campaign?.budget,
    campaign?.event?.timezone,
    campaign?.external_id,
    fetchGuidedPayloadForAdset,
    toast,
  ]);

  const openFocusedCreativeEdit = useCallback(
    async (params: { adsetId: string; creativeIndex: number }) => {
      const adset = campaign?.adsets?.find((a) => a.id === params.adsetId);
      if (adset && hasAdsetFinishedDelivery(adset)) {
        toast({
          title: "Can't edit creatives yet",
          description: ADSET_FINISHED_DELIVERY_EDIT_GUIDANCE,
          status: "warning",
        });
        return;
      }
      setIsGuidedEditPayloadLoading(true);
      try {
        const payload = await fetchGuidedPayloadForAdset(params.adsetId);
        const clampedIndex = Math.max(
          0,
          Math.min(params.creativeIndex, (payload.creatives?.length ?? 1) - 1)
        );
        const selectedCreative = payload.creatives?.[clampedIndex];
        if (!selectedCreative) {
          throw new Error("No creative found for this ad set.");
        }
        const hasAnyInstagram = (payload.creatives ?? []).some(
          (c) => String(c.instagram_id ?? "").trim() !== ""
        );
        if (!hasAnyInstagram) {
          if (!campaign?.id) {
            throw new Error("Campaign not loaded");
          }
          setGuidedFlowDraft({
            mode: "edit",
            campaignId: campaign.id,
            adsetId: params.adsetId,
            guidedEditPayload: payload,
            eventId: payload.event_id,
            adAccountId: payload.ad_account_id,
            eventTimezone: normalizeGuidedFlowEventTimezone(
              campaign.event?.timezone ?? payload.timezone
            ),
            event: campaign.event as any,
          });
          return;
        }
        setCreativeEditState({
          adsetId: params.adsetId,
          creativeIndex: clampedIndex,
          creatives: payload.creatives ?? [],
          creative: selectedCreative,
        });
      } catch (err: any) {
        toast({
          title: "Something went wrong",
          description: err?.message ?? String(err),
          status: "error",
        });
      } finally {
        setIsGuidedEditPayloadLoading(false);
      }
    },
    [campaign?.adsets, campaign?.event, campaign?.id, fetchGuidedPayloadForAdset, toast]
  );

  const saveFocusedBudgetEdit = useCallback(
    async (budget: BudgetData) => {
      if (!campaign?.id || !budgetEditState) return;
      if (
        String(campaign.external_id ?? "").trim() &&
        budget.type !== budgetEditState.budget.type
      ) {
        toast({
          title: "Budget type can't be changed",
          description:
            "Campaigns linked to Meta must keep the same budget type (daily, lifetime, or scheduled).",
          status: "error",
        });
        return;
      }
      setSavingFocusedEdit(true);
      try {
        const response = await actions.ADMIN.updateGuidedEditPayload(
          buildBudgetUpdatePayload({
            campaign_id: campaign.id,
            adset_id: budgetEditState.adsetId,
            budget,
            eventTimezone: normalizeGuidedFlowEventTimezone(
              campaign.event?.timezone
            ),
          })
        ) as GuidedEditPutResponse;
        applyGuidedEditApiCampaignToState(response);
        setBudgetEditState(null);
        const updateId = guidedEditPutUpdateId(response);
        if (updateId) {
          setGuidedPublishStatus("Publishing budget update…");
          await pollForResult({
            campaign_id: campaign.id,
            update_id: updateId,
            title: "Budget updated",
            description: "Budget update published",
            ...managedCampaignPollOptions,
          });
        } else {
          toast({ title: "Budget updated", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to update budget",
          description: err?.message ?? String(err),
          status: "error",
        });
      } finally {
        setSavingFocusedEdit(false);
      }
    },
    [
      applyGuidedEditApiCampaignToState,
      budgetEditState,
      campaign?.external_id,
      campaign?.id,
      fetchCampaign,
      managedCampaignPollOptions,
      pollForResult,
      setCampaign,
      toast,
    ]
  );

  const saveFocusedScheduleEdit = useCallback(
    async (schedule: ScheduleInput) => {
      if (!campaign?.id || !scheduleEditState) return;
      setSavingFocusedEdit(true);
      try {
        const response = await actions.ADMIN.updateGuidedEditPayload(
          buildScheduleUpdatePayload({
            campaign_id: campaign.id,
            adset_id: scheduleEditState.adsetId,
            schedule,
          })
        ) as GuidedEditPutResponse;
        applyGuidedEditApiCampaignToState(response);
        setScheduleEditState(null);
        const updateId = guidedEditPutUpdateId(response);
        if (updateId) {
          setGuidedPublishStatus("Publishing schedule update…");
          await pollForResult({
            campaign_id: campaign.id,
            update_id: updateId,
            title: "Schedule updated",
            description: "Schedule update published",
            ...managedCampaignPollOptions,
          });
        } else {
          toast({ title: "Schedule updated", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to update schedule",
          description: err?.message ?? String(err),
          status: "error",
        });
      } finally {
        setSavingFocusedEdit(false);
      }
    },
    [
      applyGuidedEditApiCampaignToState,
      campaign?.id,
      fetchCampaign,
      managedCampaignPollOptions,
      pollForResult,
      scheduleEditState,
      setCampaign,
      toast,
    ]
  );

  const saveFocusedCreativeEdit = useCallback(
    async (creative: GuidedEditPayload["creatives"][number]) => {
      if (!campaign?.id || !creativeEditState) return;
      setSavingFocusedEdit(true);
      try {
        const response = await actions.ADMIN.updateGuidedEditPayload(
          buildCreativeUpdatePayload({
            campaign_id: campaign.id,
            adset_id: creativeEditState.adsetId,
            creatives: creativeEditState.creatives,
            creativeIndex: creativeEditState.creativeIndex,
            creative,
          })
        ) as GuidedEditPutResponse;
        applyGuidedEditApiCampaignToState(response);
        setCreativeEditState(null);
        const updateId = guidedEditPutUpdateId(response);
        if (updateId) {
          setGuidedPublishStatus("Publishing creative update…");
          await pollForResult({
            campaign_id: campaign.id,
            update_id: updateId,
            title: "Creative updated",
            description: "Creative update published",
            ...managedCampaignPollOptions,
          });
        } else {
          toast({ title: "Creative updated", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to update creative",
          description: err?.message ?? String(err),
          status: "error",
        });
      } finally {
        setSavingFocusedEdit(false);
      }
    },
    [
      applyGuidedEditApiCampaignToState,
      campaign?.id,
      creativeEditState,
      fetchCampaign,
      managedCampaignPollOptions,
      pollForResult,
      setCampaign,
      toast,
    ]
  );

  const openGuidedEditForAdset = useCallback(
    /** Omit `adsetId` so the API picks the first editable ad set (same rules as focused budget/schedule). */
    async (adsetId?: string) => {
      if (!campaign?.id) return;
      const targetingSpecificAdset =
        adsetId != null && String(adsetId).trim() !== "";
      if (targetingSpecificAdset) {
        const target = campaign.adsets?.find((a) => a.id === adsetId);
        if (target && hasAdsetFinishedDelivery(target)) {
          toast({
            title: "Can't edit this ad set yet",
            description: ADSET_FINISHED_DELIVERY_EDIT_GUIDANCE,
            status: "warning",
          });
          return;
        }
      }
      setIsGuidedEditPayloadLoading(true);
      try {
        const payload = await fetchGuidedPayloadForAdset(
          targetingSpecificAdset ? adsetId : undefined
        );
        setGuidedFlowDraft({
          mode: "edit",
          campaignId: campaign.id,
          adsetId: payload.adset_id,
          guidedEditPayload: payload,
          eventId: payload.event_id,
          adAccountId: payload.ad_account_id,
          eventTimezone: normalizeGuidedFlowEventTimezone(
            campaign.event?.timezone ?? payload.timezone
          ),
          event: campaign.event as any,
        });
      } catch (err: any) {
        if (isGuidedEditNothingToLoadFetchError(err)) {
          toast({
            title: targetingSpecificAdset
              ? "You can't edit this ad set right now"
              : "You can't edit this campaign right now",
            description:
              typeof err?.message === "string"
                ? err.message
                : targetingSpecificAdset
                  ? "This ad set may be missing or no longer available to edit."
                  : "This campaign needs at least one ad set before you can use the editor.",
            status: "warning",
          });
        } else {
          toast({
            title: "Something went wrong",
            description: err?.message ?? String(err),
            status: "error",
          });
        }
      } finally {
        setIsGuidedEditPayloadLoading(false);
      }
    },
    [campaign, fetchGuidedPayloadForAdset, toast]
  );

  /** Open guided flow to create a brand-new creative and choose target adsets in-flow. */
  const openGuidedEditForNewCreative = useCallback(async () => {
    if (anyListedAdsetFinishedDelivery(campaign?.adsets)) {
      toast({
        title: "Can't add a creative yet",
        description: ADSET_FINISHED_DELIVERY_EDIT_GUIDANCE,
        status: "warning",
      });
      return;
    }
    const blocked = resolveManagedAdsetEditUnavailable(campaign?.adsets);
    if (blocked.blocked) {
      toast({
        title: "You can't add a creative right now",
        description: messageForManagedAdsetEditUnavailable(blocked),
        status: "warning",
      });
      return;
    }
    if (!campaign?.id || !campaign?.account_id) return;
    setGuidedFlowDraft({
      mode: "edit_new_creative",
      campaignId: campaign.id,
      creativeTargetAdsets: creativeTargetAdsetOptions,
      eventId: campaign.event_ids?.[0] ?? "",
      adAccountId: campaign.account_id,
      eventTimezone: normalizeGuidedFlowEventTimezone(campaign.event?.timezone),
      event: campaign.event as any,
    });
  }, [campaign, creativeTargetAdsetOptions, toast]);

  /** Open guided flow to add a new ad set (same UI as new creative, but creates new ad set on save). */
  const openGuidedEditForNewAdset = useCallback(async () => {
    if (anyListedAdsetFinishedDelivery(campaign?.adsets)) {
      toast({
        title: "Can't add a new ad set yet",
        description: ADSET_FINISHED_DELIVERY_EDIT_GUIDANCE,
        status: "warning",
      });
      return;
    }
    const firstAdsetId = campaign?.adsets?.[0]?.id;
    if (!firstAdsetId) {
      toast({
        title: "No ad sets",
        description:
          "Create an ad set from the event's Managed Ads section first — we need one to load the editor.",
        status: "warning",
      });
      return;
    }
    if (!campaign?.id) return;
    setIsGuidedEditPayloadLoading(true);
    try {
      const payload = await actions.ADMIN.fetchGuidedEditPayloadForAdset({
        campaign_id: campaign.id,
        adset_id: firstAdsetId,
      });
      const firstCreative = payload.creatives?.[0];
      const emptyCreative: GuidedEditPayload["creatives"][number] = {
        primary_text: "",
        fb_page_id: firstCreative?.fb_page_id ?? "",
        instagram_id: firstCreative?.instagram_id ?? "",
      };
      const payloadForNewAdset: GuidedEditPayload = {
        ...payload,
        adset_id: "", // no existing ad set
        adset_name: "New ad set",
        creatives: [emptyCreative],
      };
      setGuidedFlowDraft({
        mode: "edit_new_adset",
        campaignId: campaign.id,
        guidedEditPayload: payloadForNewAdset,
        eventId: payload.event_id,
        adAccountId: payload.ad_account_id,
        eventTimezone: normalizeGuidedFlowEventTimezone(
          campaign.event?.timezone ?? payload.timezone
        ),
        event: campaign.event as any,
      });
    } catch (err: any) {
      toast({
        title: "Could not open edit",
        description: err?.message ?? String(err),
        status: "error",
      });
    } finally {
      setIsGuidedEditPayloadLoading(false);
    }
  }, [campaign, toast]);

  const openEditCampaign = useCallback(() => {
    void openGuidedEditForAdset();
  }, [openGuidedEditForAdset]);

  const openEditAdset = useCallback(
    (adsetId: string) => {
      void openGuidedEditForAdset(adsetId);
    },
    [openGuidedEditForAdset]
  );

  const openEditAd = useCallback(
    (
      ad: DisplayMarketingAd,
      /** When provided (e.g. from AdSetCard), use this adset so we edit the clicked adset, not the first match. */
      adsetFromContext?: DisplayMarketingAdSet
    ) => {
      const adset =
        adsetFromContext ??
        campaign?.adsets?.find(
          (candidateAdset) =>
            candidateAdset.status !== "DELETED" &&
            candidateAdset.status !== "TO_DELETE" &&
            candidateAdset.ads?.some(
              (candidateAd) =>
                candidateAd.id === ad.id ||
                candidateAd.external_id === ad.external_id
            )
        );
      if (!adset?.id) return;
      const adId = ad.id ?? ad.external_id;
      if (!adId) return;
      const creativeIndex =
        adset.ads?.findIndex(
          (candidateAd) =>
            candidateAd.id === adId || candidateAd.external_id === adId
        ) ?? -1;
      if (creativeIndex < 0) return;
      openFocusedCreativeEdit({ adsetId: adset.id, creativeIndex });
    },
    [campaign?.adsets, openFocusedCreativeEdit]
  );

  const openFocusedCreativeEditFromInsights = useCallback(
    (adsetId: string, creativeIndex?: number) => {
      const targetAdset = campaign?.adsets?.find(
        (adset) => adset.id === adsetId
      );
      const firstCreativeIndex =
        targetAdset?.ads?.findIndex(
          (ad) => ad.status !== "DELETED" && ad.status !== "TO_DELETE"
        ) ?? 0;
      const index =
        creativeIndex !== undefined && creativeIndex >= 0
          ? creativeIndex
          : firstCreativeIndex;
      openFocusedCreativeEdit({
        adsetId,
        creativeIndex: Math.max(0, index),
      });
    },
    [campaign?.adsets, openFocusedCreativeEdit]
  );

  const mergeAdvertiserInstagramRows = useCallback((rows: InstagramAccount[]) => {
    if (!rows.length) return;
    setInstagramAccounts((prev) => mergeInstagramAccounts([...prev, ...rows]));
  }, []);

  const mergePartnerInstagramRows = useCallback((rows: InstagramAccount[]) => {
    if (!rows.length) return;
    setPartnerInstagramAccounts((prev) =>
      mergeInstagramAccounts([...prev, ...rows])
    );
  }, []);

  const fetchInstagramForFacebookPage = useCallback(
    async (facebookPageId: string, adAccountIdOverride?: string) => {
      const adAccountId =
        adAccountIdOverride?.trim() || campaign?.account_id?.trim();
      if (!adAccountId) return [];
      try {
        const rows = await fetchInstagramAccountsForAdvertiserPage({
          ad_account_id: adAccountId,
          facebook_page_id: facebookPageId,
        });
        mergeAdvertiserInstagramRows(rows);
        return rows;
      } catch {
        return [];
      }
    },
    [campaign?.account_id, mergeAdvertiserInstagramRows]
  );

  const refetchPageScopedInstagramForCreativeModal = useCallback(
    async (
      facebookPageId: string,
      scope: "advertiser" | "partner"
    ): Promise<InstagramAccount[]> => {
      if (!campaign?.account_id) return [];
      setCreativeModalInstagramLoading(true);
      try {
        const rows = await fetchInstagramAccountsForAdvertiserPage({
          ad_account_id: campaign.account_id,
          facebook_page_id: facebookPageId,
        });
        if (scope === "partner") {
          mergePartnerInstagramRows(rows);
        } else {
          mergeAdvertiserInstagramRows(rows);
        }
        return rows;
      } catch {
        return [];
      } finally {
        setCreativeModalInstagramLoading(false);
      }
    },
    [campaign?.account_id, mergeAdvertiserInstagramRows, mergePartnerInstagramRows]
  );

  const refetchInstagramForCreativeModal = useCallback(
    (facebookPageId: string) =>
      refetchPageScopedInstagramForCreativeModal(facebookPageId, "advertiser"),
    [refetchPageScopedInstagramForCreativeModal]
  );

  const refetchPartnerInstagramForCreativeModal = useCallback(
    (facebookPageId: string) =>
      refetchPageScopedInstagramForCreativeModal(facebookPageId, "partner"),
    [refetchPageScopedInstagramForCreativeModal]
  );

  useEffect(() => {
    if (!creativeEditState || !campaign?.account_id) return;
    const facebookPageId = creativeEditState.creative.fb_page_id?.trim();
    if (!facebookPageId) return;
    void refetchInstagramForCreativeModal(facebookPageId);
  }, [
    campaign?.account_id,
    creativeEditState,
    refetchInstagramForCreativeModal,
  ]);

  const handleFlowCreateCampaign = useCallback(() => {
    throw new Error("Create flow is disabled on campaign detail edit.");
  }, []);

  const handleFlowUpdateAdset = useCallback(
    async (params: GuidedSubmitParams) => {
      if (!campaign) {
        throw new Error("Campaign not loaded");
      }
      const targetAdsetIds = normalizeGuidedEditTargetAdsetIds(
        params.target_adset_ids
      );
      const adsetIdForSingleTarget = targetAdsetIds[0] ?? params.adset_id;

      const response = await actions.ADMIN.updateGuidedEditPayload({
        campaign_id: params.campaign_id,
        adset_id: adsetIdForSingleTarget,
        ...(params.creative_propagation_mode
          ? { creative_propagation_mode: params.creative_propagation_mode }
          : {}),
        ...(targetAdsetIds.length > 0
          ? { target_adset_ids: targetAdsetIds }
          : {}),
        adset_name: params.adset_name,
        ...(params.budget
          ? {
              budget: wireGuidedEditBudgetFromFormDollars(
                params.budget,
                normalizeGuidedFlowEventTimezone(campaign.event?.timezone)
              ),
            }
          : {}),
        ...(params.schedule ? { schedule: params.schedule } : {}),
        ...(params.targeting ? { targeting: params.targeting } : {}),
        creatives: params.creatives.map((creative) =>
          mapCreativeBlockToGuidedCreative(creative, { partnerPostsEnabled })
        ),
      }) as GuidedEditPutResponse;
      applyGuidedEditApiCampaignToState(response);
      const updateId = guidedEditPutUpdateId(response);
      if (updateId) {
        setGuidedPublishStatus("Publishing edit…");
        void pollForResult({
          campaign_id: params.campaign_id,
          update_id: updateId,
          title: "Edit published",
          description: "Edit published",
          ...managedCampaignPollOptions,
        }).catch(() => {
          // Best-effort polling; avoid unhandled rejection.
        });
      } else {
        setGuidedPublishStatus(null);
        await fetchCampaign({ silent: true });
      }
    },
    [
      applyGuidedEditApiCampaignToState,
      campaign,
      fetchCampaign,
      managedCampaignPollOptions,
      partnerPostsEnabled,
      pollForResult,
      setCampaign,
    ]
  );

  const handleFlowCreateAdset = useCallback(
    async (params: GuidedSubmitParams) => {
      const response = await actions.ADMIN.updateGuidedEditPayload({
        campaign_id: params.campaign_id,
        adset_id: "new",
        adset_name: params.adset_name || "New ad set",
        ...(params.budget
          ? {
              budget: wireGuidedEditBudgetFromFormDollars(
                params.budget,
                normalizeGuidedFlowEventTimezone(campaign?.event?.timezone)
              ),
            }
          : {}),
        ...(params.schedule ? { schedule: params.schedule } : {}),
        ...(params.targeting ? { targeting: params.targeting } : {}),
        creatives: params.creatives.map((creative) =>
          mapCreativeBlockToGuidedCreative(creative, { partnerPostsEnabled })
        ),
      }) as GuidedEditPutResponse;
      applyGuidedEditApiCampaignToState(response);
      const updateId = guidedEditPutUpdateId(response);
      if (updateId) {
        setGuidedPublishStatus("Publishing edit…");
        void pollForResult({
          campaign_id: params.campaign_id,
          update_id: updateId,
          title: "Edit published",
          description: "Edit published",
          ...managedCampaignPollOptions,
        }).catch(() => {
          // Best-effort polling; avoid unhandled rejection.
        });
      } else {
        setGuidedPublishStatus(null);
        await fetchCampaign({ silent: true });
      }
    },
    [
      applyGuidedEditApiCampaignToState,
      campaign,
      fetchCampaign,
      managedCampaignPollOptions,
      partnerPostsEnabled,
      pollForResult,
    ]
  );

  const handleFlowSubmitGuidedEdit = useCallback(
    async (params: GuidedSubmitParams) => {
      if (params.mode === "edit_new_adset") {
        await handleFlowCreateAdset(params);
        return;
      }

      await handleFlowUpdateAdset(params);
    },
    [handleFlowCreateAdset, handleFlowUpdateAdset]
  );

  const handleGenerateIntents = useCallback(async () => {
    if (!campaign?.id) return;
    setGeneratingIntents(true);
    try {
      await actions.ADMIN.runCampaignIntents(campaign.id);
      toast({
        title: "Intents generation started",
        description:
          "It should take ~1:30 minutes to complete -- refresh in a minute to see new recommendations.",
        status: "success",
      });
      await fetchCampaign();
    } catch (err: any) {
      toast({
        title: "Failed to start intents",
        description: err?.message,
        status: "error",
      });
    } finally {
      setGeneratingIntents(false);
    }
  }, [campaign?.id, fetchCampaign, toast]);

  if (guidedFlowDraft) {
    return (
      <GuidedCreateCampaignFlowScreen
        draft={guidedFlowDraft}
        adAccounts={
          campaign.account_id
            ? [{ id: campaign.account_id, name: campaign.account_id }]
            : []
        }
        pages={pages}
        partnerPages={partnerPages}
        partnerInstagramAccounts={partnerInstagramAccounts}
        instagramAccounts={instagramAccounts}
        pixels={pixels}
        isRelatedDataLoading={isRelatedDataLoading}
        partnershipOptionsLoading={partnershipOptionsLoading}
        partnershipOptionsError={partnershipOptionsError}
        partnershipPermissionsLoaded={partnershipPermissionsLoaded}
        advertiserValidationMessages={advertiserValidationMessages}
        metaBusinessId={metaBusinessId}
        partnershipCatalogByIdentityKey={catalogByKey}
        onRefetchPartnershipPartnerOptions={refetchPartnershipPartnerOptions}
        createUploadUrl={actions.ADMIN.createPublicUploadUrl}
        onBack={() => {
          setGuidedPublishStatus(null);
          setGuidedFlowDraft(null);
        }}
        onLaunchSuccess={() => {
          setGuidedFlowDraft(null);
        }}
        toast={toast}
        onFetchInstagramForFacebookPage={fetchInstagramForFacebookPage}
        onMergeAdvertiserInstagramAccounts={mergeAdvertiserInstagramRows}
        onMergePartnerInstagramAccounts={mergePartnerInstagramRows}
        handleCreateCampaign={handleFlowCreateCampaign}
        handleSubmitGuidedEdit={handleFlowSubmitGuidedEdit}
        publishStatusText={guidedPublishStatus}
        organizationId={campaign.organization_id}
        search={{
          interests: searchInterests,
          geo_markets: searchGeoMarkets,
        }}
      />
    );
  }

  if (loading) {
    return (
      <VStack py={8}>
        <Spinner />
        <Text color="gray.500">Loading campaign...</Text>
      </VStack>
    );
  }

  if (error || !campaign) {
    return (
      <VStack py={8}>
        <Text color="red.500">{error ?? "Campaign not found"}</Text>
        {/* @ts-ignore */}
        <Button as={RouterLink} to="/campaigns/ads" variant="link">
          ← Back to Meta Campaigns
        </Button>
      </VStack>
    );
  }

  const isManagedPublishPolling = Boolean(
    campaign_id && managedLoading.publish[campaign_id]
  );

  const isApplyingCampaignChanges =
    savingFocusedEdit ||
    Boolean(guidedPublishStatus) ||
    isPublishingCampaign ||
    isPausingCampaign ||
    publishingAdsetId != null ||
    pausingAdsetId != null ||
    activatingAdKey != null ||
    pausingAdKey != null ||
    isManagedPublishPolling ||
    isPublishPollingActive ||
    isMetaImportApplyLoading;

  return (
    <>
      <VStack w="full" h="full" minH={0} flex={1} gap={0} align="stretch">
        <VStack
          w="full"
          align="flex-start"
          gap={2}
          p={4}
          borderBottom="1px solid #E6E6E6"
        >
          <HStack w="full" justify="space-between" align="flex-start" gap={2}>
            <VStack align="flex-start" gap={1} flex={1} minW={0}>
              <ChakraLink
                as={RouterLink}
                to="/campaigns/ads"
                fontFamily="Work Sans"
                fontWeight={500}
                fontSize="14px"
                color="#888888"
                letterSpacing="-0.03em"
                verticalAlign="middle"
                _hover={{ textDecoration: "underline" }}
              >
                ← Meta Campaigns
              </ChakraLink>
              <HStack
                w="full"
                flexDirection={{ base: "column", md: "row" }}
                gap={2}
              >
                <Box
                  w="full"
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  columnGap={2}
                  rowGap={1}
                  justifyContent={isNarrow ? "center" : "flex-start"}
                  minW={0}
                >
                  <ChakraLink
                    as="span"
                    fontWeight="500"
                    fontSize="24px"
                    lineHeight="30px"
                    letterSpacing="-0.03em"
                    verticalAlign="middle"
                    noOfLines={2}
                    minW={0}
                    w={isNarrow ? "full" : "auto"}
                    whiteSpace="normal"
                    overflowWrap="anywhere"
                    textAlign={isNarrow ? "center" : "left"}
                    _hover={{ opacity: 0.85 }}
                    sx={{ leadingTrim: "none" }}
                    cursor={hasPlatformScopes ? "pointer" : undefined}
                    title={
                      hasPlatformScopes
                        ? "Double-click for Meta campaign id, Ads Manager, and import"
                        : undefined
                    }
                    onDoubleClick={
                      hasPlatformScopes
                        ? () => {
                            onOpenMetaTools();
                          }
                        : undefined
                    }
                  >
                    {campaign.name}
                  </ChakraLink>
                  <MetaSyncStatusLabel
                    status={campaign.status}
                    hasExternalId={!!campaign.external_id}
                  />
                  <CampaignPublishProgressIndicator
                    executionId={headerPublishLockToken}
                    guidedPublishStatus={guidedPublishStatus}
                    serverPublishStep={serverPublishStep}
                    publishLoadOutcome={publishLoadOutcome}
                  />
                </Box>
                <HStack
                  gap={2}
                  alignSelf="center"
                  width={{ base: "full", md: "auto" }}
                >
                  <Tooltip
                    label={ADSET_FINISHED_DELIVERY_EDIT_GUIDANCE}
                    hasArrow
                    placement="bottom-start"
                    maxW="320px"
                    isDisabled={!addMenuBlockedByPastAdsetSchedule}
                    shouldWrapChildren
                  >
                    <Menu placement="bottom-start">
                      <MenuButton
                        as={Button}
                        bg="black"
                        color="white"
                        borderColor="black"
                        _hover={{ bg: "gray.800" }}
                        _active={{
                          bg: "black",
                          color: "white",
                          borderColor: "black",
                        }}
                        _expanded={{
                          bg: "black",
                          color: "white",
                          borderColor: "black",
                        }}
                        w={{ base: "full", md: undefined }}
                        h={{ base: "52px", md: undefined }}
                        padding={{ base: 0, md: "0 20px" }}
                        rightIcon={
                          <ChevronDownIcon marginRight={{ base: 4, md: 0 }} />
                        }
                        isDisabled={
                          !campaign.adsets?.length ||
                          isApplyingCampaignChanges ||
                          addMenuBlockedByPastAdsetSchedule
                        }
                      >
                        Add
                      </MenuButton>
                      <MenuList borderRadius="lg" minW="180px">
                        <MenuItem
                          onClick={openGuidedEditForNewCreative}
                          isDisabled={
                            isApplyingCampaignChanges ||
                            addMenuBlockedByPastAdsetSchedule
                          }
                        >
                          New Ad Creative
                        </MenuItem>
                        <MenuItem
                          onClick={openGuidedEditForNewAdset}
                          isDisabled={
                            isApplyingCampaignChanges ||
                            addMenuBlockedByPastAdsetSchedule
                          }
                        >
                          New Ad Set
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Tooltip>
                  {campaign.status === "ACTIVE" ? (
                    <Button
                      w={{ base: "full", md: undefined }}
                      h={{ base: "52px", md: undefined }}
                      padding={{ base: 0, md: "0 20px" }}
                      boxShadow="0px 4px 10px 0px #0000001A"
                      onClick={handlePauseCampaign}
                      isLoading={isPausingCampaign}
                      isDisabled={
                        isPausingCampaign || isApplyingCampaignChanges
                      }
                    >
                      Pause Campaign
                    </Button>
                  ) : campaign.status !== "FROZEN" ? (
                    <Button
                      w={{ base: "full", md: undefined }}
                      h={{ base: "52px", md: undefined }}
                      padding={{ base: 0, md: "0 20px" }}
                      bg="gray.800"
                      color="white"
                      borderColor="gray.800"
                      _hover={{ bg: "gray.700", opacity: 0.9 }}
                      boxShadow="0px 4px 10px 0px #0000001A"
                      onClick={() => handlePublishCampaign({ id: campaign.id })}
                      isLoading={isPublishingCampaign}
                      isDisabled={
                        isPublishingCampaign || isApplyingCampaignChanges
                      }
                    >
                      {campaign.status === "PAUSED"
                        ? "Resume"
                        : "Activate Campaign"}
                    </Button>
                  ) : null}
                </HStack>
              </HStack>
            </VStack>
          </HStack>
        </VStack>

        {/* On narrow viewports: tabs at top */}
        {isNarrow && (
          <Box
            w="full"
            p={4}
            borderBottom="1px solid"
            borderColor="gray.200"
            bg="white"
          >
            <TabSelector
              size="sm"
              optionId={mobileTab}
              onOptionChange={(optionId) =>
                setMobileTab(optionId as "overview" | "insights")
              }
              options={[
                { id: "overview", label: "Overview", content: null },
                { id: "insights", label: "Insights & Ad sets", content: null },
              ]}
              spacing={2}
            />
          </Box>
        )}

        <Stack
          w="full"
          flex={1}
          minH={0}
          spacing={0}
          direction={{ base: "column", lg: "row" }}
          align="stretch"
        >
          {/* Left bar: campaign overview — matches Meta Campaigns detail layout. On narrow, only show when Overview tab is selected. */}
          {(!isNarrow || mobileTab === "overview") && (
            <Box
              w={{ base: "100%", lg: "auto" }}
              minW={{ base: "100%", lg: "375px", xl: "475px" }}
              maxW={{ base: "100%", lg: "40%" }}
              minH={0}
              overflowY="auto"
              bg="white"
              p={4}
            >
              <VStack align="stretch" spacing={5}>
                <CampaignDetailsCard
                  campaign={campaign}
                  onEditBudget={openFocusedBudgetEdit}
                  onEditSchedule={openFocusedScheduleEdit}
                  isApplyingChanges={isApplyingCampaignChanges}
                />
                <Divider borderColor="gray.200" />
                <CampaignResultsCard campaign={campaign} />
                <Divider borderColor="gray.200" />
                <CampaignMetricsCard campaign={campaign} />
              </VStack>
            </Box>
          )}

          {/* Right: insights + ad sets. On narrow, only show when Insights tab is selected. */}
          {(!isNarrow || mobileTab === "insights") && (
            <RightPanel
              campaign={campaign}
              pages={pages}
              instagramAccounts={instagramAccounts}
              onEditCampaign={openEditCampaign}
              onEditAdset={openEditAdset}
              onEditAd={openEditAd}
              onRefetch={fetchCampaign}
              onRefetchSilent={handleRefetchSilent}
              onPauseCampaign={handlePauseCampaign}
              onPublishCampaign={handlePublishCampaign}
              onUnpublishCampaign={handleUnpublishCampaign}
              onPublishAdset={handlePublishAdset}
              onPublishAd={handlePublishAd}
              onUnpublishAdset={handleUnpublishAdset}
              onUnpublishAd={handleUnpublishAd}
              isPublishingCampaign={isPublishingCampaign}
              activatingAdKey={activatingAdKey}
              pausingAdsetId={pausingAdsetId}
              publishingAdsetId={publishingAdsetId}
              pausingAdKey={pausingAdKey}
              onOpenFocusedCreativeEdit={openFocusedCreativeEditFromInsights}
              isApplyingChanges={isApplyingCampaignChanges}
              pollPublishAfterIntentApply={pollPublishAfterIntentApply}
            />
          )}
        </Stack>

        {budgetEditState && (
          <EditBudgetModal
            isOpen={!!budgetEditState}
            initialBudget={budgetEditState.budget}
            eventTimezone={normalizeGuidedFlowEventTimezone(
              campaign?.event?.timezone
            )}
            scheduledBudgetDeliveryWindow={
              campaign?.schedule
                ? {
                    deliveryWindowStartIso: campaign.schedule.start_time,
                    deliveryWindowEndIso: campaign.schedule.stop_time,
                  }
                : null
            }
            linkedToMeta={Boolean(campaign?.external_id)}
            onClose={() => setBudgetEditState(null)}
            onSave={saveFocusedBudgetEdit}
            isSaving={savingFocusedEdit}
          />
        )}
        {scheduleEditState && (
          <EditScheduleModal
            isOpen={!!scheduleEditState}
            eventTimezone={scheduleEditState.eventTimezone}
            initialSchedule={scheduleEditState.schedule}
            isStartTimeLocked={scheduleEditState.isStartTimeLocked}
            isScheduleRangeReadOnly={
              scheduleEditState.isScheduleRangeReadOnly === true
            }
            scheduleReadOnlyHelperText={
              scheduleEditState.scheduleReadOnlyHelperText
            }
            onClose={() => setScheduleEditState(null)}
            onSave={saveFocusedScheduleEdit}
            isSaving={savingFocusedEdit}
          />
        )}
        {creativeEditState && (
          <EditAdCreativeModal
            isOpen={!!creativeEditState}
            creative={creativeEditState.creative}
            pages={pages}
            partnerPages={partnerPages}
            partnerInstagramAccounts={partnerInstagramAccounts}
            instagramAccounts={instagramAccounts}
            createUploadUrl={actions.ADMIN.createPublicUploadUrl}
            onClose={() => setCreativeEditState(null)}
            onSave={saveFocusedCreativeEdit}
            onFacebookPageChange={refetchInstagramForCreativeModal}
            onPartnerFacebookPageChange={refetchPartnerInstagramForCreativeModal}
            isSaving={savingFocusedEdit}
            isFacebookPagesLoading={isRelatedDataLoading}
            isInstagramAccountsLoading={creativeModalInstagramLoading}
            otherAdsInAdsetHaveInstagram={(
              creativeEditState.creatives ?? []
            ).some(
              (c, i) =>
                i !== creativeEditState.creativeIndex &&
                String(c.instagram_id ?? "").trim() !== ""
            )}
            adAccountId={campaign.account_id}
            partnershipOptionsLoading={partnershipOptionsLoading}
            partnershipPermissionsLoaded={partnershipPermissionsLoaded}
            partnershipOptionsError={partnershipOptionsError}
            metaBusinessId={metaBusinessId}
            advertiserValidationMessages={advertiserValidationMessages}
            onRefetchPartnershipPartnerOptions={refetchPartnershipPartnerOptions}
            partnershipCatalogByIdentityKey={catalogByKey}
            organizationId={campaign.organization_id}
          />
        )}

        <MetaCampaignToolsModal
          isOpen={isMetaToolsOpen}
          onClose={onCloseMetaTools}
          campaign={campaign}
          canImportFromMeta={canImportFromMeta}
          isApplyingCampaignChanges={isApplyingCampaignChanges}
          onImportApplyLoadingChange={setIsMetaImportApplyLoading}
          onAfterImport={() => void fetchCampaign({ silent: true })}
        />
      </VStack>
      {isGuidedEditPayloadLoading ? (
        <Modal
          isOpen
          onClose={() => {}}
          isCentered
          closeOnOverlayClick={false}
          closeOnEsc={false}
        >
          <ModalOverlay />
          <ModalContent
            w="220px"
            py={8}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="gray.800"
            borderRadius="xl"
          >
            <VStack spacing={3}>
              <KYDLoading withText="Loading campaign data..." />
            </VStack>
          </ModalContent>
        </Modal>
      ) : null}
    </>
  );
}
